import { CommonModule, CurrencyPipe } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { forkJoin, Observable, switchMap } from 'rxjs';
import { AbstractControl, FormArray, FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { NgbTooltip } from '@ng-bootstrap/ng-bootstrap';
import { NgSelectComponent } from '@ng-select/ng-select';
import {
  BadgeComponent,
  CardActionsComponent,
  CardBodyComponent,
  CardComponent,
  CardHeaderComponent,
  CardHeaderTitleDirective,
  CardFooterAction,
  CustomerDetailsCardComponent,
  CustomerDetailsData,
  GroundworksTableComponent,
  GroundworksTableData,
  StateMessageComponent,
  TableGroup,
} from '@groundworks/ngx-groundworks-ui';
import { BatchUpdateCustomerSatisfactionVisitsResponse, BatchUpdateProjectInstallDatesResponse, CrewMemberResponse, CustomerSatisfactionVisitInfo, InstallDatePanelInfo, InstallDatePanelProjectInfo, ProblemDetails, ProjectInstallDateInfo, SwaggerException, UserInfo } from '../../sdk/sdk';
import {
  BatchAddInstallDateItem,
  BatchRemoveInstallDateItem,
  BatchUpdateInstallDateItem,
  CalendarService,
  CrewMemberAllocation,
} from '../../services/calendar.service';
import { InstallDatePanelService } from '../../services/install-date-panel.service';
import { ConfigService } from '../../config.service';
import { ProjectStatusConstants } from '../../constants/project-status.constants';
import { formatUtcIsoDate } from '../../utils/iso-date';
import {
  ForemanOption,
  ForemanRole,
  InstallDateFormData,
  InstallDateRowSnapshot,
  installDateRowsEqual,
  snapshotInstallDateRow,
} from '../../models/install-event.types';

/**
 * Maps the user's `department` string (from the Foremen API) to the role discriminator
 * used by the dropdown's role-grouping. Mirrors the same function in
 * `WeekViewCalendarComponent` so the panel can enrich its own foreman list independently.
 */
function departmentToRole(department: string | undefined): ForemanRole {
  switch (department) {
    case 'Sub-Contractor': return 'sub-contractor';
    case 'Co-Foreman': return 'co-foreman';
    case 'Field Superintendent': return 'field-superintendent';
    default: return 'foreman';
  }
}

function savePayloadIsEmpty(payload: { updates: unknown[]; additions: unknown[]; removals: unknown[] }): boolean {
  return payload.updates.length === 0 && payload.additions.length === 0 && payload.removals.length === 0;
}

/**
 * Window for the panel's foreman + availability read. We only schedule into the future,
 * so a generous forward window covers realistic picks; the back-edge covers any existing
 * rows so historical foremen are still classified correctly.
 */
const PROJECT_BRANCH_FETCH_DAYS_BACK = 30;
const PROJECT_BRANCH_FETCH_DAYS_FORWARD = 365;

function mapUsersToForemanOptions(users: UserInfo[]): ForemanOption[] {
  return users
    .filter(u => !!u.id)
    .map(u => ({
      id: u.id,
      name: u.name ?? u.id,
      role: departmentToRole(u.department),
    }));
}

function toIsoDateKey(value: Date | string): string {
  if (typeof value !== 'string') return formatUtcIsoDate(value);
  const datePart = /^\d{4}-\d{2}-\d{2}/.exec(value);
  return datePart ? datePart[0] : formatUtcIsoDate(new Date(value));
}

/**
 * Converts the endpoint's per-foreman availability dates into the ISO-date sets the
 * row validator compares against. See {@link rowAvailabilityError}.
 */
function toAvailabilityMap(
  availabilityByForeman: Record<string, readonly (Date | string)[]> | undefined,
): Map<string, Set<string>> {
  const out = new Map<string, Set<string>>();
  for (const [foremanId, dates] of Object.entries(availabilityByForeman ?? {})) {
    out.set(foremanId, new Set((dates ?? []).map(toIsoDateKey)));
  }
  return out;
}

/**
 * Server `errorCode` for a save refused because it would schedule work on a cancelled job —
 * see CancelledProjectScheduleException. Arrives as HTTP 400, so it must be matched before the
 * 409 branch in buildSaveError.
 */
const CANCELLED_SCHEDULE_ERROR_CODE = 'CANCELLED_PROJECT_SCHEDULE_LOCKED';

const CREW_JOB_PROFILES = new Set([
  'Co-Foreman',
  'Installer',
  'Field Superintendent',
  'Field Superintendent II',
]);

interface CrewOption {
  userId: string;
  name: string | undefined;
}

interface SaveError {
  banner: string | null;
  rowErrors: Record<number, string>;
}

interface SaveSpec {
  isCsv: boolean;
  run: () => Observable<BatchUpdateProjectInstallDatesResponse | BatchUpdateCustomerSatisfactionVisitsResponse>;
}

interface ProjectFormState {
  form: FormGroup;
  originalInstallDateIds: Set<string>;
  originalInstallDateRows: Map<string, InstallDateRowSnapshot>;
  originalCustomerSatisfactionVisitIds: Set<string>;
  originalCustomerSatisfactionVisitRows: Map<string, InstallDateRowSnapshot>;
  saveError: SaveError | null;
  saveAttempted: boolean;
  initialized: boolean;
  customerSatisfactionVisitsInitialized: boolean;
}

@Component({
  selector: 'app-schedule-project-panel',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    NgbTooltip,
    NgSelectComponent,
    BadgeComponent,
    CardComponent,
    CardBodyComponent,
    CardActionsComponent,
    CardHeaderComponent,
    CardHeaderTitleDirective,
    StateMessageComponent,
    CustomerDetailsCardComponent,
    GroundworksTableComponent,
  ],
  templateUrl: './schedule-project-panel.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [CurrencyPipe],
})
export class ScheduleProjectPanelComponent {
  private readonly installDatePanelService = inject(InstallDatePanelService);
  private readonly calendarService = inject(CalendarService);
  private readonly currencyPipe = inject(CurrencyPipe);
  private readonly destroyRef = inject(DestroyRef);
  private readonly fb = inject(FormBuilder);
  protected readonly configService = inject(ConfigService);

  readonly opportunityId = input<string>('');
  readonly projectId = input<string>('');
  readonly branchId = input<string | undefined>(undefined);
  readonly initialInstallDates = input<InstallDateFormData[]>([]);
  readonly crewsLoading = input<boolean>(false);
  readonly customerSatisfactionVisit = input<boolean>(false);

  readonly saved = output<void>();
  readonly goBackToJob = output<void>();
  readonly exitEditMode = output<void>();
  readonly dateRemoved = output<{ date: string; foremanUserId: string }>();
  /** Emitted when the user switches tabs so the parent can attribute subsequent
   *  calendar picks to the newly-active project. */
  readonly projectChanged = output<string>();

  readonly panel = signal<InstallDatePanelInfo | undefined>(undefined);
  readonly isLoading = signal(false);
  readonly hasError = signal(false);
  readonly isSaving = signal(false);

  readonly showLoading = computed(() =>
    this.isLoading() ||
    this.crewsLoading()
  );
  readonly showContent = computed(() => !this.showLoading() && !this.hasError() && !!this.panel());

  /**
   * Foreman list for the *project's* branch, as resolved by the panel endpoint.
   * Sole source of truth for the dropdown — see {@link foremanByRole}. MOD-3651.
   * `undefined` = not fetched yet; an empty array means "fetched, nobody at this branch".
   */
  private readonly projectBranchForemen = signal<ForemanOption[] | undefined>(undefined);

  /**
   * Per-foreman availability dates at the *project's* branch, as resolved by the panel
   * endpoint. Keyed by foremanUserId, value is the set of ISO date strings
   * (YYYY-MM-DD) on which the foreman has at least one non-deleted ForemanAvailability
   * row at the project's branch. Used by {@link rowAvailabilityError} to mark rows
   * whose (foreman, date) would 409 server-side. MOD-3651.
   */
  private readonly branchAvailabilityByForeman = signal<Map<string, Set<string>>>(new Map());

  /** Available crew options cached by foremanUserId. */
  private readonly crewOptions = new Map<string, CrewOption[]>();
  /** Preferred crew cached by foremanUserId — applied as the default selection on foreman change. */
  private readonly preferredCrew = new Map<string, CrewMemberResponse[]>();
  /** Foreman IDs whose crew is currently being fetched. */
  private readonly crewLoadingSet = new Set<string>();
  /** userId → displayName for crew members from server data, used to show allocated people not in the active users list. */
  private readonly allocatedCrewNames = new Map<string, string>();

  /** Save error for the currently-viewed project. Mirrored from per-project state by an effect. */
  readonly saveError = signal<SaveError | null>(null);
  /** True after the user has attempted a save/navigate on the current project. Resets on form change. */
  readonly saveAttempted = signal(false);

  /** Per-project form state keyed by project id. Mutation-heavy; reactivity is carried by `sessionVersion`. */
  private readonly states = new Map<string, ProjectFormState>();
  /** Snapshot of foremanUserId → foremanUserName for past install-date rows.
   *  Used to synthesise a display-only option when the historical foreman is no
   *  longer in the active project-branch foreman list. */
  private readonly historicalForemanNames = new Map<string, string>();
  private readonly sessionVersion = signal(0);
  private readonly emptyForm: FormGroup = this.fb.group({
    installDates: this.fb.array([]),
    customerSatisfactionVisits: this.fb.array([]),
  });

  readonly minDate = this.todayAsDateString();

  readonly projects = computed<InstallDatePanelProjectInfo[]>(
    () => this.panel()?.projects ?? []);
  readonly selectedProjectIndex = signal(0);
  private hasSeededProjectIndex = false;

  readonly project = computed<InstallDatePanelProjectInfo | undefined>(() =>
    this.projects()[this.selectedProjectIndex()]
  );

  readonly headerTitle = computed(() => {
    const project = this.project();
    const prefix = this.isCurrentProjectCsvMode()
      ? 'Schedule Customer Satisfaction Visit'
      : (project?.installDates?.length ?? 0) > 0 ? 'Edit Install Dates' : 'Add Install Dates';
    const typeName = project?.projectTypeName ?? '';
    const netAmount = project?.projectNetAmount;
    const formatted = netAmount != null ? this.currencyPipe.transform(netAmount, 'USD', 'symbol', '1.2-2') : '';
    return `${prefix}: ${typeName} ${formatted}`.trim();
  });

  readonly customerData = computed<CustomerDetailsData | undefined>(() => {
    const opp = this.panel();
    if (!opp?.address) return undefined;
    const address = opp.address;
    return {
      address: {
        streetAddress: address.streetAddress ?? '',
        streetAddress2: address.streetAddress2 ?? '',
        city: address.city ?? '',
        state: address.state ?? '',
        postalCode: address.postalCode ?? '',
      },
      contacts: (opp.contacts ?? []).map(c => ({
        firstName: c.firstName ?? '',
        lastName: c.lastName ?? '',
        relationshipToProperty: c.relationship ?? '',
        phoneNumber: c.phone ?? '',
        email: c.email ?? '',
      })),
    };
  });

  readonly productsTableData = computed<GroundworksTableData>(() => {
    const products = this.project()?.boundProducts ?? [];
    const rows: TableGroup['rows'] = [
      ...products.map(bp => ({
        type: 'data' as const,
        cells: [
          { value: bp.name ?? '' },
          { value: `${bp.quantity} ${bp.unitOfMeasure ?? ''}` }
        ],
      })),
    ];
    return {
      columns: [
        { label: 'Installable Product' },
        { label: 'Units' }
      ],
      groups: [{ label: '', rows }],
    };
  });

  readonly foremanByRole = computed(() => {
    // MOD-3651: sole source is the panel endpoint's branch-scoped foreman list. Until
    // it resolves, the dropdown is empty — but the panel is gated behind showLoading()
    // during the fetch, so the template never renders an empty dropdown.
    const options = this.projectBranchForemen() ?? [];
    const byName = (a: ForemanOption, b: ForemanOption) => a.name.localeCompare(b.name);
    return {
      foreman: options.filter(f => f.role === 'foreman').sort(byName),
      subContractor: options.filter(f => f.role === 'sub-contractor').sort(byName),
      coForeman: options.filter(f => f.role === 'co-foreman').sort(byName),
      fieldSuperintendent: options.filter(f => f.role === 'field-superintendent').sort(byName),
    };
  });

  private static readonly FOREMAN_GROUP_LABELS: Record<string, string> = {
    foreman: 'Foreman',
    subContractor: 'Sub-Contractor',
    coForeman: 'Co-Foreman',
    fieldSuperintendent: 'Field Superintendent',
  };

  private static flattenForemanOptions(
    byRole: { foreman: ForemanOption[]; subContractor: ForemanOption[]; coForeman: ForemanOption[]; fieldSuperintendent: ForemanOption[] },
  ): { id: string; name: string; group: string }[] {
    const result: { id: string; name: string; group: string }[] = [];
    for (const [key, label] of Object.entries(ScheduleProjectPanelComponent.FOREMAN_GROUP_LABELS)) {
      for (const f of byRole[key as keyof typeof byRole]) {
        result.push({ id: f.id, name: f.name, group: label });
      }
    }
    return result;
  }

  readonly foremanOptions = computed(() =>
    ScheduleProjectPanelComponent.flattenForemanOptions(this.foremanByRole()),
  );

  /**
   * Returns the foreman option list for a specific row, split by role.
   *
   * For past (disabled) rows, if the saved foremanUserId is not present in the
   * active project-branch list, a synthetic entry is prepended under the 'foreman'
   * role so the disabled <select> always displays the historical foreman's name.
   * Future (enabled) rows always see the unmodified active list.
   */
  foremanByRoleForRow(rowIndex: number): { foreman: ForemanOption[]; subContractor: ForemanOption[]; coForeman: ForemanOption[]; fieldSuperintendent: ForemanOption[] } {
    const control = this.installDateArray.at(rowIndex);
    const isPastRow = this.isPastDate(rowIndex);

    if (!isPastRow) {
      return this.foremanByRole();
    }

    const currentOptions = this.projectBranchForemen() ?? [];
    const savedId = control.get('foremanUserId')?.value as string | undefined;
    const alreadyPresent = !savedId || currentOptions.some(f => f.id === savedId);

    if (alreadyPresent) {
      return this.foremanByRole();
    }

    const historicalName = this.historicalForemanNames.get(savedId!) ?? savedId!;
    const synthetic: ForemanOption = { id: savedId!, name: historicalName, role: 'foreman' };
    const merged = [synthetic, ...currentOptions];
    const byName = (a: ForemanOption, b: ForemanOption) => a.name.localeCompare(b.name);
    return {
      foreman: merged.filter(f => f.role === 'foreman').sort(byName),
      subContractor: merged.filter(f => f.role === 'sub-contractor').sort(byName),
      coForeman: merged.filter(f => f.role === 'co-foreman').sort(byName),
      fieldSuperintendent: merged.filter(f => f.role === 'field-superintendent').sort(byName),
    };
  }

  foremanOptionsForRow(rowIndex: number): { id: string; name: string; group: string }[] {
    if (!this.isPastDate(rowIndex)) {
      return this.foremanOptions();
    }
    return ScheduleProjectPanelComponent.flattenForemanOptions(this.foremanByRoleForRow(rowIndex));
  }

  foremanOptionsForCsvRow(rowIndex: number): { id: string; name: string; group: string }[] {
    if (!this.isPastCsvDate(rowIndex)) {
      return this.foremanOptions();
    }
    return ScheduleProjectPanelComponent.flattenForemanOptions(this.foremanByRole());
  }

  /**
   * Returns the per-row availability error message, or `null` when the row's
   * (foreman, date) has at least one non-deleted ForemanAvailability row at the
   * project's branch. MOD-3651 — pre-validates the same constraint the server
   * enforces, so the user gets an inline reason before submitting.
   *
   * Skipped for past (disabled) rows: they're read-only and never re-validated.
   * Skipped when we don't yet have an availability map (fetch in flight, or the server
   * resolved none): we let the existing server-side 409 path handle it.
   */
  rowAvailabilityError(rowIndex: number): string | null {
    const isCsvMode = this.isCurrentProjectCsvMode();
    const array = isCsvMode ? this.customerSatisfactionVisitArray : this.installDateArray;
    const control = array.at(rowIndex);
    const dateValue = control?.get('date')?.value as string ?? '';
    if (!control || this.isDateInPast(dateValue)) return null;

    // MOD-3715: intentional ADD-vs-MOVE asymmetry.
    // ADD rows (installDateId === '') are new slots — eager client-side pre-validation
    // gives the user inline feedback before the request is sent.
    // MOVE rows (installDateId !== '') are existing install dates whose date was edited.
    // For moves, defer availability enforcement to the server's 409 response, which the
    // client already renders per-row via buildSaveError. Pre-empting that path here would
    // block save with a client message that can never be dismissed without losing the move.
    const installDateId = control.get('installDateId')?.value as string;
    if (installDateId) return null;

    const map = this.branchAvailabilityByForeman();
    if (map.size === 0) return null;

    const foremanUserId = control.get('foremanUserId')?.value as string;
    const date = control.get('date')?.value as string;
    if (!foremanUserId || !date) return null;

    const dates = map.get(foremanUserId);
    if (!dates) {
      // Foreman has zero rows at the project's branch in the fetch window —
      // mirror the server's FOREMAN_NOT_AT_BRANCH classification.
      return 'Foreman is not assigned to this project\'s branch.';
    }
    if (!dates.has(date)) {
      // Foreman IS at the branch but has no slot on this date — mirror the server's
      // FOREMAN_UNAVAILABLE_ON_DATE classification with the same date-only vocabulary.
      return `Foreman unavailable on ${date}.`;
    }
    return null;
  }

  /** Returns crew member options for the given foreman. Populated lazily when the dropdown opens. */
  crewOptionsForForeman(foremanId: string): CrewOption[] {
    if (!foremanId) return [];
    const options = this.crewOptions.get(foremanId);
    if (options) return options;
    if (this.allocatedCrewNames.size === 0) return [];
    return [...this.allocatedCrewNames].map(([userId, name]) => ({ userId, name }));
  }

  /** True while crew options are being fetched for the given foreman. */
  isCrewLoadingForForeman(foremanId: string): boolean {
    return !!foremanId && this.crewLoadingSet.has(foremanId);
  }

  /** Clears the crew selection for the row and fetches available + preferred crew for the new foreman. */
  onForemanChange(foremanId: string, rowIndex: number): void {
    const crewArr = this.getCrewAllocationsArray(rowIndex);
    crewArr.clear();
    this.installDateArray.at(rowIndex).get('foremanAllocationPercentage')?.setValue(null);
    // Crew-locked rows (imported hours / pre-go-live) get no crew: the previous foreman's crew
    // can't carry over, and the row's crew fields are read-only so nothing can be picked.
    if (this.isInstallDateRowCrewLocked(rowIndex)) {
      this.disableCrewFields(this.installDateArray.at(rowIndex) as FormGroup);
      this.sessionVersion.update(v => v + 1);
      return;
    }
    if (!foremanId) return;
    this.ensureCrewOptionsLoaded(foremanId, () => {
      const preferred = this.preferredCrew.get(foremanId) ?? [];
      if (preferred.length > 0) {
        crewArr.clear();
        for (const crew of preferred) {
          if (!crew.userId) continue;
          crewArr.push(this.createCrewAllocationGroup(crew.userId));
        }
      }
      this.sessionVersion.update(v => v + 1);
    });
  }

  onCsvForemanChange(foremanId: string, rowIndex: number): void {
    const crewArr = this.getCsvCrewAllocationsArray(rowIndex);
    crewArr.clear();
    this.customerSatisfactionVisitArray.at(rowIndex).get('foremanAllocationPercentage')?.setValue(null);
    // See onForemanChange: a crew-locked row keeps no crew across a foreman change.
    if (this.isCsvRowCrewLocked(rowIndex)) {
      this.disableCrewFields(this.customerSatisfactionVisitArray.at(rowIndex) as FormGroup);
      this.sessionVersion.update(v => v + 1);
      return;
    }
    if (!foremanId) return;
    this.ensureCrewOptionsLoaded(foremanId, () => {
      const preferred = this.preferredCrew.get(foremanId) ?? [];
      if (preferred.length > 0) {
        crewArr.clear();
        for (const crew of preferred) {
          if (!crew.userId) continue;
          crewArr.push(this.createCrewAllocationGroup(crew.userId));
        }
      }
      this.sessionVersion.update(v => v + 1);
    });
  }

  /**
   * Loads branch crew options + preferred crew for a foreman into the per-foreman caches, then
   * invokes onReady. Used both when the foreman changes (to default the selection) and
   * when rows are first built (to populate the dropdown without touching the selection),
   * so the crew dropdown is filled the moment edit mode opens on an existing assignment.
   */
  private ensureCrewOptionsLoaded(foremanId: string, onReady?: () => void): void {
    if (!foremanId) return;
    if (this.crewOptions.has(foremanId)) {
      this.mergeAllocatedCrewIntoOptions(this.crewOptions.get(foremanId)!);
      onReady?.();
      return;
    }
    if (this.crewLoadingSet.has(foremanId)) return;
    this.crewLoadingSet.add(foremanId);
    this.sessionVersion.update(v => v + 1);
    forkJoin([
      this.calendarService.getAssignedPreferredCrew(foremanId),
      this.calendarService.getForemanDetail(foremanId).pipe(
        switchMap(detail => this.calendarService.getActiveUsers(detail.homeBranchId)),
      ),
    ]).pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: ([preferred, allBranchUsers]) => {
          const options = this.buildAndSortCrewOptions(allBranchUsers);
          this.mergeAllocatedCrewIntoOptions(options);
          this.crewOptions.set(foremanId, options);
          this.preferredCrew.set(foremanId, preferred);
          this.crewLoadingSet.delete(foremanId);
          onReady?.();
          this.sessionVersion.update(v => v + 1);
        },
        error: () => {
          this.crewLoadingSet.delete(foremanId);
          this.sessionVersion.update(v => v + 1);
        },
      });
  }

  /**
   * Every crew-eligible person at the foreman's home branch.
   *
   * Deliberately does NOT consult `available-crew` (PersonForemanAssignment). That table is
   * preferred-crew *configuration* — one row per person, so it is exclusive by construction —
   * and it is read by the Foreman Configuration panel and the PPJ bonus grid, where one-to-one
   * is correct. Scheduling asks a different question ("who can work this install day"), which is
   * many-to-many: being on one foreman's default crew must not make someone unschedulable under
   * another. The external field-app crew-assign endpoint already treats it that way, so filtering
   * here produced rows the panel could not render. See also `buildSavePayload` — no save-side
   * guard reads PersonForemanAssignment either.
   *
   * `isActive` is filtered here because `getActiveUsers` does not filter it server-side despite
   * the name; `available-crew` used to supply that filter incidentally.
   */
  private buildAndSortCrewOptions(allBranchUsers: UserInfo[]): CrewOption[] {
    return allBranchUsers
      .filter(u => u.isActive)
      .filter(u => CREW_JOB_PROFILES.has(u.jobProfile ?? ''))
      .map(u => ({ userId: u.id, name: u.name }))
      .sort((a, b) => (a.name ?? '').localeCompare(b.name ?? ''));
  }

  private mergeAllocatedCrewIntoOptions(options: CrewOption[]): void {
    const present = new Set(options.map(o => o.userId));
    for (const [userId, name] of this.allocatedCrewNames) {
      if (!present.has(userId)) {
        options.push({ userId, name });
        present.add(userId);
      }
    }
    options.sort((a, b) => (a.name ?? '').localeCompare(b.name ?? ''));
  }

  /** Per-project CSV mode: true when the current project has a completionDate. */
  readonly isCurrentProjectCsvMode = computed(() =>
    !!this.project()?.completionDate
  );

  /**
   * True when the work on screen is cancelled, at either level.
   *
   * `Job.cancelledAt` is the authoritative signal; the project's own Cancelled status is a
   * cascade-derived copy that can be missing for jobs cancelled before the cascade existed. Both
   * are checked so a job-cancelled/project-unstamped row — the exact data shape whose install dates
   * the calendar now surfaces — is still recognised as cancelled here.
   *
   * Cancellation no longer freezes the whole row. It locks the date/foreman/time fields and
   * withdraws add/remove, while crew allocation stays editable on eligible rows: the surviving
   * rows are days the crew actually worked, and their labour still has to be attributed.
   */
  readonly isCurrentProjectCancelled = computed<boolean>(() =>
    this.project()?.projectStatusId === ProjectStatusConstants.Cancelled
    || !!this.panel()?.job?.cancelledAt
  );

  /**
   * True when the project on screen has been stamped Descoped (MOD-4990). Before MOD-4990 a
   * descoped project was soft-deleted and never reached this panel; now it stays live and this is
   * the only thing standing between a descoped project's install history and a scheduler editing it
   * through a sibling tab or a stale/direct URL.
   */
  readonly isCurrentProjectDescoped = computed<boolean>(() =>
    this.project()?.projectStatusId === ProjectStatusConstants.Descoped
  );

  /**
   * The combined scheduling lock. Cancellation and descoping close a project to new/moved work for
   * the same reason — no future work belongs on either — so every guard that used to check
   * {@link isCurrentProjectCancelled} alone now checks this instead. Cancellation still owns its own
   * wording (tooltip/badge) where the two need to say something different.
   */
  readonly isCurrentProjectLocked = computed<boolean>(() =>
    this.isCurrentProjectCancelled() || this.isCurrentProjectDescoped()
  );

  readonly canSubmit = computed<boolean>(() => {
    this.sessionVersion();
    this.selectedProjectIndex();
    // Re-subscribe to the availability map so canSubmit recomputes after the fetch.
    this.branchAvailabilityByForeman();
    const activeArray = this.isCurrentProjectCsvMode()
      ? this.customerSatisfactionVisitArray
      : this.installDateArray;
    if (!this.form.valid || activeArray.length === 0 || this.isSaving()) {
      return false;
    }
    // MOD-3651: block save when any non-disabled row's (foreman, date) is missing
    // availability at the project's branch. Past (disabled) rows are exempt — they
    // are read-only and never re-validated.
    for (let i = 0; i < activeArray.length; i++) {
      if (this.rowAvailabilityError(i) !== null) return false;
    }
    return true;
  });

  readonly visibleRows = computed(() => {
    this.sessionVersion();
    this.selectedProjectIndex();
    const result: { index: number; control: FormGroup }[] = [];
    for (let i = 0; i < this.installDateArray.controls.length; i++) {
      result.push({ index: i, control: this.installDateArray.at(i) as FormGroup });
    }
    return result;
  });

  readonly visibleCsvRows = computed(() => {
    this.sessionVersion();
    this.selectedProjectIndex();
    const result: { index: number; control: FormGroup }[] = [];
    for (let i = 0; i < this.customerSatisfactionVisitArray.controls.length; i++) {
      result.push({ index: i, control: this.customerSatisfactionVisitArray.at(i) as FormGroup });
    }
    return result;
  });

  retryButton = { text: 'Try Again', onClick: (): void => this.loadPanel() };

  readonly hasUnsavedChanges = computed<boolean>(() => {
    this.sessionVersion();
    for (const [projectId, state] of this.states.entries()) {
      if (this.projectHasChanges(state, this.isCsvModeForProjectId(projectId))) return true;
    }
    return false;
  });

  readonly canPersistAllChanges = computed<boolean>(() => {
    this.sessionVersion();
    for (const [projectId, state] of this.states.entries()) {
      if (this.projectHasChanges(state, this.isCsvModeForProjectId(projectId)) && !state.form.valid) return false;
    }
    return true;
  });

  readonly cardActions = computed<CardFooterAction[]>(() => [
    {
      label: 'View Job Details',
      type: 'secondary',
      disabled: (): boolean => this.isSaving(),
      action: (): void => this.onGoBackToJob(),
    },
    {
      label: this.hasUnsavedChanges() ? 'Save & Exit Edit Mode' : 'Exit Edit Mode',
      type: 'primary',
      action: (): void => this.onExitEditMode(),
      disabled: (): boolean => this.isSaving() || !this.canPersistAllChanges(),
      loading: (): boolean => this.isSaving(),
    },
  ]);

  constructor() {
    effect(() => {
      const id = this.opportunityId();
      if (id) this.loadPanel();
    });

    // Lazily create per-project state and (re-)apply install dates whenever
    // the project list or calendar-pending dates change. Each appended entry
    // carries a `targetProjectId` set by the parent at pick time; entries
    // without one fall back to the `projectId` input (the project the user
    // was actively scheduling when they entered edit mode). Each state's
    // dirty-guard preserves in-flight edits.
    effect(() => {
      const projects = this.projects();
      const appended = this.initialInstallDates();
      const fallbackProjectId = this.projectId();
      // Job-level cancellation is authoritative and cascades to every project on the job —
      // see isCurrentProjectCancelled. Read once here so each project's lock decision agrees
      // with what the header badge reports.
      const jobCancelled = !!this.panel()?.job?.cancelledAt;
      if (projects.length === 0) return;

      for (const proj of projects) {
        if (!proj.id) continue;
        let state = this.states.get(proj.id);
        if (!state) {
          state = this.createProjectState(proj.id);
          this.states.set(proj.id, state);
        }
        const isCsvMode = !!proj.completionDate;
        // Cancelled and descoped are locked identically here (see isCurrentProjectLocked) — both
        // close the project to new/moved work while leaving surviving rows' crew editable.
        const isLocked = proj.projectStatusId === ProjectStatusConstants.Cancelled
          || jobCancelled
          || proj.projectStatusId === ProjectStatusConstants.Descoped;
        const appendedForProject = appended.filter(d => {
          const target = d.targetProjectId ?? fallbackProjectId;
          return target === proj.id;
        });
        this.applyInstallDates(
          state,
          proj.installDates ?? [],
          isCsvMode ? [] : appendedForProject,
          isCsvMode,
          isLocked,
        );
        this.applyCustomerSatisfactionVisits(
          state,
          proj.customerSatisfactionVisits ?? [],
          isCsvMode ? appendedForProject : [],
          isLocked,
        );
      }
      this.sessionVersion.update(v => v + 1);
      this.syncDisplayState();
    });
  }

  /** Current project's form. Returns an empty sentinel when no project is loaded. */
  get form(): FormGroup {
    const id = this.project()?.id;
    if (!id) return this.emptyForm;
    return this.states.get(id)?.form ?? this.emptyForm;
  }

  get installDateArray(): FormArray {
    return this.form.get('installDates') as FormArray;
  }

  get customerSatisfactionVisitArray(): FormArray {
    return this.form.get('customerSatisfactionVisits') as FormArray;
  }

  private static readonly LOCKED_TOOLTIP = 'Locked: Date in past / hours imported';
  /** Cancelled rows lock date/foreman/time for a different reason, so they say so. */
  private static readonly CANCELLED_TOOLTIP = 'Locked: Job cancelled';
  /** Descoped rows lock for the same reason a cancelled row does, worded for its own disposition. */
  private static readonly DESCOPED_TOOLTIP = 'Locked: Project descoped';

  isPastDate(index: number): boolean {
    const dateValue: string = (this.installDateArray.at(index).get('date')?.value as string) ?? '';
    return this.isDateInPast(dateValue);
  }

  isPastCsvDate(index: number): boolean {
    const dateValue: string = (this.customerSatisfactionVisitArray.at(index).get('date')?.value as string) ?? '';
    return this.isDateInPast(dateValue);
  }

  rowLockTooltip(index: number): string {
    if (this.isPastDate(index)) return ScheduleProjectPanelComponent.LOCKED_TOOLTIP;
    return this.lockedProjectTooltip();
  }

  csvRowLockTooltip(index: number): string {
    if (this.isPastCsvDate(index)) return ScheduleProjectPanelComponent.LOCKED_TOOLTIP;
    return this.lockedProjectTooltip();
  }

  /** Cancellation wins the wording when a project is somehow both — see {@link isCurrentProjectLocked}. */
  private lockedProjectTooltip(): string {
    if (this.isCurrentProjectCancelled()) return ScheduleProjectPanelComponent.CANCELLED_TOOLTIP;
    if (this.isCurrentProjectDescoped()) return ScheduleProjectPanelComponent.DESCOPED_TOOLTIP;
    return '';
  }

  crewLockTooltip(index: number): string {
    return this.isInstallDateRowCrewLocked(index) ? ScheduleProjectPanelComponent.LOCKED_TOOLTIP : '';
  }

  csvCrewLockTooltip(index: number): string {
    return this.isCsvRowCrewLocked(index) ? ScheduleProjectPanelComponent.LOCKED_TOOLTIP : '';
  }

  selectProject(index: number): void {
    if (index === this.selectedProjectIndex()) return;
    if (index < 0 || index >= this.projects().length) return;
    this.performSaveCurrent(() => {
      this.selectedProjectIndex.set(index);
      this.syncDisplayState();
      this.sessionVersion.update(v => v + 1);
      const newId = this.project()?.id;
      if (newId) this.projectChanged.emit(newId);
    });
  }

  onGoBackToJob(): void {
    this.performSaveCurrent(() => this.goBackToJob.emit());
  }

  onExitEditMode(): void {
    this.performSaveCurrent(() => this.exitEditMode.emit());
  }

  saveEvent(postSaveAction: 'exit' | 'goBack' = 'exit'): void {
    this.performSaveCurrent(() => {
      if (postSaveAction === 'goBack') {
        this.goBackToJob.emit();
      } else {
        this.exitEditMode.emit();
      }
    });
  }

  /**
   * Save the currently-viewed project if it has pending changes, then run `onSuccess`.
   * If the form is invalid the save is skipped and `onSuccess` is not called.
   * Other projects are saved at the moment the user switches tabs, so they are
   * always clean by the time this runs.
   */
  private isCsvModeForProjectId(projectId: string): boolean {
    return !!this.projects().find(p => p.id === projectId)?.completionDate;
  }

  private performSaveCurrent(onSuccess: () => void): void {
    if (this.isSaving()) return;

    const projectId = this.project()?.id;
    const state = projectId ? this.states.get(projectId) : undefined;
    // Capture CSV mode now — the project may change before the async callback fires.
    const isCsvMode = this.isCurrentProjectCsvMode();

    if (!projectId || !state || !this.projectHasChanges(state, isCsvMode)) {
      onSuccess();
      return;
    }
    if (!state.form.valid) return;

    state.saveAttempted = true;
    this.isSaving.set(true);
    state.saveError = null;
    this.syncDisplayState();
    this.sessionVersion.update(v => v + 1);

    this.runSavesSequentially(this.buildSaveSpecs(projectId, state, isCsvMode), 0, state, onSuccess);
  }

  /**
   * Up to two sequential batch calls per save (MOD-4733): a completed project can hold
   * dirty install-date allocation edits AND dirty CSV visit rows in the same session.
   * Install dates go first; empty payloads are skipped entirely.
   */
  private buildSaveSpecs(projectId: string, state: ProjectFormState, isCsvMode: boolean): SaveSpec[] {
    const specs: SaveSpec[] = [];
    const installPayload = this.buildSavePayload(state, false);
    if (!savePayloadIsEmpty(installPayload)) {
      specs.push({
        isCsv: false,
        run: () => this.calendarService.batchUpdateInstallDates(
          projectId, installPayload.updates, installPayload.additions, installPayload.removals, this.todayAsDateString()),
      });
    }
    if (isCsvMode) {
      const csvPayload = this.buildSavePayload(state, true);
      if (!savePayloadIsEmpty(csvPayload)) {
        specs.push({
          isCsv: true,
          run: () => this.calendarService.batchUpdateCustomerSatisfactionVisits(
            projectId, csvPayload.updates, csvPayload.additions, csvPayload.removals, this.todayAsDateString()),
        });
      }
    }
    return specs;
  }

  /**
   * Runs the save specs one at a time. Each success is applied immediately, so if a
   * later spec fails, the earlier persisted rows keep their server ids and refreshed
   * snapshots — a retry re-sends only the un-persisted diff. The form goes pristine
   * only after every spec succeeds.
   */
  private runSavesSequentially(specs: SaveSpec[], index: number, state: ProjectFormState, onSuccess: () => void): void {
    if (index >= specs.length) {
      this.isSaving.set(false);
      state.form.markAsPristine();
      this.saved.emit();
      this.syncDisplayState();
      this.sessionVersion.update(v => v + 1);
      onSuccess();
      return;
    }
    const spec = specs[index];
    spec.run()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: response => {
          this.applySaveResult(state, response, spec.isCsv);
          this.runSavesSequentially(specs, index + 1, state, onSuccess);
        },
        error: (err: unknown) => {
          this.isSaving.set(false);
          state.saveError = this.buildSaveError(err, state, spec.isCsv);
          this.syncDisplayState();
          this.sessionVersion.update(v => v + 1);
        },
      });
  }

  /**
   * Patch a just-saved project's form with the server's new ids so a subsequent
   * save (e.g., after the user fixes a sibling project's conflict) doesn't
   * re-add rows that were already persisted.
   */
  private applySaveResult(
    state: ProjectFormState,
    response: BatchUpdateProjectInstallDatesResponse | BatchUpdateCustomerSatisfactionVisitsResponse,
    isCsvMode: boolean,
  ): void {
    const arrayName = isCsvMode ? 'customerSatisfactionVisits' : 'installDates';
    const arr = state.form.get(arrayName) as FormArray;
    const added = response.added ?? [];

    // Match each added row to a form row that still has no installDateId.
    // Track assigned controls so two additions on the same date+foreman
    // (unlikely but possible) don't both claim the same row.
    const assigned = new Set<number>();
    for (const a of added) {
      const addedDate = formatUtcIsoDate(a.date);
      const addedForeman = a.foremanUserId ?? '';
      for (let i = 0; i < arr.length; i++) {
        if (assigned.has(i)) continue;
        const row = arr.at(i);
        if (row.get('installDateId')?.value) continue;
        const rowDate = row.get('date')?.value as string;
        const rowForeman = row.get('foremanUserId')?.value as string;
        if (rowDate === addedDate && rowForeman === addedForeman) {
          row.get('installDateId')?.setValue(a.id);
          assigned.add(i);
          break;
        }
      }
    }

    const nextOriginalIds = new Set<string>();
    for (const c of arr.controls) {
      const id = c.get('installDateId')?.value as string;
      if (id) nextOriginalIds.add(id);
    }
    const nextOriginalRows = this.snapshotRowMap(arr);

    if (isCsvMode) {
      state.originalCustomerSatisfactionVisitIds = nextOriginalIds;
      state.originalCustomerSatisfactionVisitRows = nextOriginalRows;
    } else {
      state.originalInstallDateIds = nextOriginalIds;
      state.originalInstallDateRows = nextOriginalRows;
    }
  }

  private buildSavePayload(state: ProjectFormState, isCsvMode: boolean): {
    updates: BatchUpdateInstallDateItem[];
    additions: BatchAddInstallDateItem[];
    removals: BatchRemoveInstallDateItem[];
  } {
    const arrayName = isCsvMode ? 'customerSatisfactionVisits' : 'installDates';
    const originalIds = isCsvMode
      ? state.originalCustomerSatisfactionVisitIds
      : state.originalInstallDateIds;
    const arr = state.form.get(arrayName) as FormArray;
    const rows = arr.controls.map(c => ({
      disabled: c.disabled,
      value: c.getRawValue() as InstallDateFormData & { isAllDay: boolean },
    }));

    const additions: BatchAddInstallDateItem[] = rows
      .filter(r => !r.value.installDateId)
      .map(r => ({
        date: r.value.date,
        foremanUserId: r.value.foremanUserId,
        startTime: r.value.isAllDay ? undefined : (r.value.startTime || undefined),
        endTime: r.value.isAllDay ? undefined : (r.value.endTime || undefined),
        crewMembers: this.buildCrewMembersForSave(r.value),
      }));

    const originalRows = isCsvMode
      ? state.originalCustomerSatisfactionVisitRows
      : state.originalInstallDateRows;

    // Disabled rows are read-only by design (past install dates, cancelled projects, or
    // completed-project rows with imported hours — MOD-4733). Exclude them from updates
    // so they do not trigger the backend's AgainstPastDate guard.
    // Unchanged rows are also excluded: an "update" on the backend is a soft-
    // delete + insert, which fans out legacy CalendarEvent delete/create events.
    // Sending only diffs avoids that churn for rows the user did not touch.
    const updates: BatchUpdateInstallDateItem[] = rows
      .filter(r => !r.disabled && !!r.value.installDateId)
      .filter(r => {
        const original = originalRows.get(r.value.installDateId);
        return !original || !installDateRowsEqual(original, snapshotInstallDateRow(r.value));
      })
      .map(r => ({
        id: r.value.installDateId,
        date: r.value.date,
        foremanUserId: r.value.foremanUserId,
        startTime: r.value.isAllDay ? undefined : (r.value.startTime || undefined),
        endTime: r.value.isAllDay ? undefined : (r.value.endTime || undefined),
        crewMembers: this.buildCrewMembersForSave(r.value),
      }));

    // Past rows are still "kept" — they must not be treated as removals.
    const keptIds = new Set(rows.filter(r => r.value.installDateId).map(r => r.value.installDateId));
    const removals: BatchRemoveInstallDateItem[] = [...originalIds]
      .filter(id => !keptIds.has(id))
      .map(id => ({ id }));

    return { updates, additions, removals };
  }

  private buildCrewMembersForSave(value: InstallDateFormData & { isAllDay: boolean }): CrewMemberAllocation[] {
    const members: CrewMemberAllocation[] = [];
    if (value.foremanUserId) {
      members.push({
        userId: value.foremanUserId,
        allocationPercentage: value.foremanAllocationPercentage ?? null,
      });
    }
    for (const crew of value.crewAllocations ?? []) {
      if (crew.userId) {
        members.push({
          id: crew.id || undefined,
          userId: crew.userId,
          allocationPercentage: crew.allocationPercentage ?? null,
        });
      }
    }
    return members;
  }

  private buildSaveError(err: unknown, state: ProjectFormState, isCsv: boolean): SaveError {
    const fallback = isCsv
      ? 'An error occurred while saving the customer satisfaction visits. Please try again.'
      : 'An error occurred while saving the install dates. Please try again.';

    // The cancelled-work scheduling refusal is a 400, not a 409, and it names an actionable cause
    // ("a future date cannot be added to a cancelled job"). Surface the server's message verbatim
    // rather than the generic banner, which would tell the user to retry something that can never
    // succeed. Matched on errorCode so the wording stays owned by the server.
    const cancelledMessage = this.cancelledScheduleMessage(err);
    if (cancelledMessage) {
      return { banner: cancelledMessage, rowErrors: {} };
    }

    if (this.extractStatus(err) !== 409) {
      return { banner: fallback, rowErrors: {} };
    }
    const problem = this.extractProblemDetails(err);
    if (!problem) {
      return { banner: 'Foreman unavailable on the selected date.', rowErrors: {} };
    }

    // OUTSIDE_SCHEDULING_WINDOW (CSV-only) returns top-level { code, message }; surface the
    // server message verbatim so the user sees "...within N days of project completion."
    if (problem['code'] === 'OUTSIDE_SCHEDULING_WINDOW') {
      const message = this.asStringOrNull(problem['message']);
      return { banner: message ?? fallback, rowErrors: {} };
    }

    // Backend emits either an aggregate `failedRows: [...]` (post-MOD-3420 batch handler)
    // or legacy singular top-level fields. Treat both uniformly by iterating a row list.
    const failedRows = Array.isArray(problem['failedRows'])
      ? (problem['failedRows'] as Record<string, unknown>[])
      : [problem];

    const arrayName = isCsv ? 'customerSatisfactionVisits' : 'installDates';
    const controls = (state.form.get(arrayName) as FormArray).controls;
    const rowErrors: Record<number, string> = {};
    for (const row of failedRows) {
      const failedForemanUserId = this.asStringOrNull(row['failedForemanUserId']);
      const failedDate = this.asStringOrNull(row['failedDate']);
      const failedInstallDateId = this.asStringOrNull(row['failedInstallDateId']);
      if (!failedForemanUserId && !failedDate && !failedInstallDateId) continue;
      const rowIndex = this.matchFailedRow(controls, failedInstallDateId, failedForemanUserId, failedDate);
      if (rowIndex !== null) {
        const crewName = this.crewMemberNameById(this.asStringOrNull(row['failedCrewMemberUserId']));
        rowErrors[rowIndex] = this.availabilityErrorMessage(row['reason'], failedDate, crewName);
      }
    }

    if (Object.keys(rowErrors).length === 0) {
      const firstRow = failedRows[0];
      const firstDate = this.asStringOrNull(firstRow?.['failedDate']);
      const firstCrewName = this.crewMemberNameById(this.asStringOrNull(firstRow?.['failedCrewMemberUserId']));
      return {
        banner: this.availabilityErrorMessage(firstRow?.['reason'], firstDate, firstCrewName),
        rowErrors: {},
      };
    }
    return { banner: null, rowErrors };
  }

  /**
   * The server's message when a save was refused for scheduling work on a cancelled job
   * (`CANCELLED_PROJECT_SCHEDULE_LOCKED`, HTTP 400), or null when this isn't that refusal.
   * `detail` carries the message on a DomainException ProblemDetails; `message` is accepted as a
   * fallback for the shape other conflict responses use.
   */
  private cancelledScheduleMessage(err: unknown): string | null {
    const problem = this.extractProblemDetails(err);
    if (!problem) return null;
    if (problem['errorCode'] !== CANCELLED_SCHEDULE_ERROR_CODE) return null;
    return this.asStringOrNull(problem['detail']) ?? this.asStringOrNull(problem['message']);
  }

  private extractStatus(err: unknown): number | undefined {
    if (err instanceof HttpErrorResponse) return err.status;
    if (SwaggerException.isSwaggerException(err)) return (err as SwaggerException).status;
    if (err instanceof ProblemDetails) { const s = err.status; return typeof s === 'number' ? s : undefined; }
    return undefined;
  }

  private extractProblemDetails(err: unknown): Record<string, unknown> | null {
    if (err instanceof HttpErrorResponse) {
      return typeof err.error === 'object' && err.error !== null
        ? (err.error as Record<string, unknown>)
        : null;
    }
    if (SwaggerException.isSwaggerException(err)) {
      const body = (err as SwaggerException).response;
      try {
        return typeof body === 'string'
          ? (JSON.parse(body) as Record<string, unknown>)
          : (body as Record<string, unknown>);
      } catch {
        return null;
      }
    }
    if (err instanceof ProblemDetails) return err as unknown as Record<string, unknown>;
    return null;
  }

  private asStringOrNull(v: unknown): string | null {
    return typeof v === 'string' ? v : null;
  }

  /**
   * Translates a server availability conflict reason (MOD-3715) and the failed date into
   * a user-facing message.  The reason discriminator mirrors the server's
   * `AvailabilityConflictReason` enum serialised as a wire string by
   * `ExceptionHandlingMiddleware.ToWireReason`.
   *
   * - `FOREMAN_NOT_AT_BRANCH`      → branch-assignment message (foreman never appears at this branch)
   * - `CREW_UNAVAILABLE_ON_DATE`   → crew member already scheduled elsewhere for that date (MOD-3334)
   * - `FOREMAN_UNAVAILABLE_ON_DATE` (or unknown/absent) → date-specific message
   */
  private availabilityErrorMessage(reason: unknown, failedDate: string | null, crewMemberName?: string | null): string {
    if (reason === 'FOREMAN_NOT_AT_BRANCH') {
      return "Foreman is not assigned to this project's branch.";
    }
    if (reason === 'CREW_UNAVAILABLE_ON_DATE') {
      const who = crewMemberName ?? 'A selected crew member';
      return failedDate
        ? `${who} is already scheduled on ${failedDate}.`
        : `${who} is already scheduled on the selected date.`;
    }
    if (reason !== 'FOREMAN_UNAVAILABLE_ON_DATE' && reason !== undefined && reason !== null) {
      console.warn('[availabilityErrorMessage] Unrecognised AvailabilityConflictReason:', reason);
    }
    return failedDate
      ? `Foreman unavailable on ${failedDate}.`
      : 'Foreman unavailable on the selected date.';
  }

  private crewMemberNameById(userId: string | null): string | null {
    if (!userId) return null;
    for (const options of this.crewOptions.values()) {
      const match = options.find(o => o.userId === userId);
      if (match?.name) return match.name;
    }
    return null;
  }

  private matchFailedRow(
    controls: AbstractControl[],
    failedInstallDateId: string | null,
    failedForemanUserId: string | null,
    failedDate: string | null,
  ): number | null {
    if (failedInstallDateId) {
      for (let i = 0; i < controls.length; i++) {
        if ((controls[i].get('installDateId')?.value as string) === failedInstallDateId) return i;
      }
      return null;
    }

    if (!failedForemanUserId || !failedDate) return null;

    const matches: number[] = [];
    for (let i = 0; i < controls.length; i++) {
      const id = controls[i].get('installDateId')?.value as string;
      if (id) continue;
      const foremanUserId = controls[i].get('foremanUserId')?.value as string;
      const date = controls[i].get('date')?.value as string;
      if (foremanUserId === failedForemanUserId && date === failedDate) matches.push(i);
    }

    return matches.length === 1 ? matches[0] : null;
  }

  /**
   * True when the row's date may be removed. Past rows are never removable (they are worked
   * history), and on cancelled or descoped work nothing is — including today's row, which is the
   * one row cancellation can leave behind un-past. See {@link isCurrentProjectLocked}.
   */
  canRemoveInstallDateRow(index: number): boolean {
    if (this.isCurrentProjectLocked()) return false;
    return !this.isPastDate(index);
  }

  canRemoveCsvRow(index: number): boolean {
    if (this.isCurrentProjectLocked()) return false;
    return !this.isPastCsvDate(index);
  }

  addDay(): void {
    // Cancelled or descoped work takes no new install days — mirrors the calendar-pick guard in
    // applyInstallDates so neither entry point can add one.
    if (this.isCurrentProjectLocked()) return;
    const lastIndex = this.installDateArray.length - 1;
    const lastDateValue = lastIndex >= 0
      ? (this.installDateArray.at(lastIndex).get('date')?.value as string ?? '')
      : '';
    const lastForemanId = lastIndex >= 0
      ? (this.installDateArray.at(lastIndex).get('foremanUserId')?.value as string ?? '')
      : '';

    const candidate = lastDateValue ? this.addOneDayToDateString(lastDateValue) : this.todayAsDateString();
    const nextDate = this.isDateInPast(candidate) ? this.todayAsDateString() : candidate;

    // No placeholder crew row — the foreman row's + adds the first one on demand.
    const crewInit = this.fb.array([] as FormGroup[]);
    const newGroup = this.fb.group({
      installDateId: [''],
      date: [nextDate, Validators.required],
      startTime: [''],
      endTime: [''],
      foremanUserId: [lastForemanId, Validators.required],
      isAllDay: [true],
      foremanAllocationPercentage: [null as number | null, [Validators.min(0), Validators.max(100)]],
      hasLaborCosts: [false],
      crewAllocations: crewInit,
    });

    if (this.isBeforeCrewGoLive(nextDate)) {
      this.disableCrewFields(newGroup);
    }

    this.installDateArray.push(newGroup);
  }

  removeRow(index: number): void {
    if (!this.canRemoveInstallDateRow(index)) return;
    this.removeRowFromArray(this.installDateArray, index);
  }

  removeCsvRow(index: number): void {
    if (!this.canRemoveCsvRow(index)) return;
    this.removeRowFromArray(this.customerSatisfactionVisitArray, index);
  }

  getCrewAllocationsArray(rowIndex: number): FormArray {
    return this.installDateArray.at(rowIndex).get('crewAllocations') as FormArray;
  }

  getCsvCrewAllocationsArray(rowIndex: number): FormArray {
    return this.customerSatisfactionVisitArray.at(rowIndex).get('crewAllocations') as FormArray;
  }

  addCrewMember(rowIndex: number): void {
    if (this.isInstallDateRowCrewLocked(rowIndex)) return;
    this.getCrewAllocationsArray(rowIndex).push(this.createCrewAllocationGroup());
    this.sessionVersion.update(v => v + 1);
  }

  addCsvCrewMember(rowIndex: number): void {
    if (this.isCsvRowCrewLocked(rowIndex)) return;
    this.getCsvCrewAllocationsArray(rowIndex).push(this.createCrewAllocationGroup());
    this.sessionVersion.update(v => v + 1);
  }

  private createCrewAllocationGroup(userId = '', allocationPercentage: number | null = null, id = ''): FormGroup {
    return this.fb.group({
      id: [id],
      userId: [userId],
      allocationPercentage: [allocationPercentage, [Validators.min(0), Validators.max(100)]],
    });
  }

  removeCrewMember(rowIndex: number, crewIndex: number): void {
    const crewArray = this.getCrewAllocationsArray(rowIndex);
    // Removing the last crew member leaves the row with none, which brings the foreman row's
    // + back rather than leaving an empty picker behind.
    crewArray.removeAt(crewIndex);
    // FormArray.removeAt does not itself mark the form dirty (MOD-4733); a pure removal with
    // no other edit must still be recognised as a pending change by projectHasChanges.
    crewArray.markAsDirty();
    this.sessionVersion.update(v => v + 1);
  }

  removeCsvCrewMember(rowIndex: number, crewIndex: number): void {
    const crewArray = this.getCsvCrewAllocationsArray(rowIndex);
    // Removing the last crew member leaves the row with none, which brings the foreman row's
    // + back rather than leaving an empty picker behind.
    crewArray.removeAt(crewIndex);
    // FormArray.removeAt does not itself mark the form dirty (MOD-4733); a pure removal with
    // no other edit must still be recognised as a pending change by projectHasChanges.
    crewArray.markAsDirty();
    this.sessionVersion.update(v => v + 1);
  }

  clampPercentage(control: AbstractControl, event: Event): void {
    const input = event.target as HTMLInputElement;
    const val = input.valueAsNumber;
    if (Number.isNaN(val)) return;
    if (val < 0) control.setValue(0);
    else if (val > 100) control.setValue(100);
  }

  clearForeman(rowIndex: number): void {
    this.installDateArray.at(rowIndex).get('foremanUserId')?.setValue('');
    this.installDateArray.at(rowIndex).get('foremanAllocationPercentage')?.setValue(null);
  }

  private removeRowFromArray(arr: FormArray, index: number): void {
    const row = arr.at(index);
    const installDateId = row.get('installDateId')?.value as string;
    const date = row.get('date')?.value as string;
    const foremanUserId = row.get('foremanUserId')?.value as string;

    arr.removeAt(index);

    const id = this.project()?.id;
    const state = id ? this.states.get(id) : undefined;
    if (state) state.saveError = null;
    this.saveError.set(null);

    if (!installDateId) {
      this.dateRemoved.emit({ date, foremanUserId });
    }
  }

  private loadPanel(): void {
    const id = this.opportunityId();
    if (!id) return;
    this.isLoading.set(true);
    this.hasError.set(false);

    const { startDate, endDate } = this.projectBranchFetchWindow();

    this.installDatePanelService.getPanel(id, startDate, endDate)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: panel => {
          if (!this.hasSeededProjectIndex) {
            const matchIndex = (panel.projects ?? []).findIndex(p => p.id === this.projectId());
            if (matchIndex >= 0) {
              this.selectedProjectIndex.set(matchIndex);
            }
            this.hasSeededProjectIndex = true;
          }
          this.panel.set(panel);
          this.projectBranchForemen.set(mapUsersToForemanOptions(panel.foremen ?? []));
          this.branchAvailabilityByForeman.set(toAvailabilityMap(panel.availabilityByForeman));
          this.isLoading.set(false);
          if (!panel.job?.branchId) this.hasError.set(true);
        },
        error: () => {
          this.isLoading.set(false);
          this.hasError.set(true);
        },
      });
  }

  private projectBranchFetchWindow(): { startDate: Date; endDate: Date } {
    const start = new Date();
    start.setHours(0, 0, 0, 0);
    start.setDate(start.getDate() - PROJECT_BRANCH_FETCH_DAYS_BACK);

    const end = new Date();
    end.setHours(0, 0, 0, 0);
    end.setDate(end.getDate() + PROJECT_BRANCH_FETCH_DAYS_FORWARD);
    return { startDate: start, endDate: end };
  }

  private createProjectState(projectId: string): ProjectFormState {
    const form = this.fb.group({
      installDates: this.fb.array([]),
      customerSatisfactionVisits: this.fb.array([]),
    });
    const state: ProjectFormState = {
      form,
      originalInstallDateIds: new Set(),
      originalInstallDateRows: new Map(),
      originalCustomerSatisfactionVisitIds: new Set(),
      originalCustomerSatisfactionVisitRows: new Map(),
      saveError: null,
      saveAttempted: false,
      initialized: false,
      customerSatisfactionVisitsInitialized: false,
    };
    form.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        if (state.saveError !== null) state.saveError = null;
        state.saveAttempted = false;
        if (this.project()?.id === projectId) {
          this.saveError.set(null);
          this.saveAttempted.set(false);
        }
        this.sessionVersion.update(v => v + 1);
      });
    return state;
  }

  private syncDisplayState(): void {
    const id = this.project()?.id;
    const state = id ? this.states.get(id) : undefined;
    this.saveError.set(state?.saveError ?? null);
    this.saveAttempted.set(state?.saveAttempted ?? false);
  }

  /**
   * Diff-apply: on first call, seed the form with server-existing rows; on every
   * call, merge in any appended entries (calendar picks) that aren't already
   * represented in the form. Post-first-call, the form is authoritative — we
   * never clear it, so subsequent effect re-fires (tab switch, new pick on a
   * different tab, etc.) can't clobber post-save or in-flight state.
   */
  private applyInstallDates(
    state: ProjectFormState,
    existing: ProjectInstallDateInfo[],
    appended: InstallDateFormData[],
    isCompleted: boolean,
    isLocked: boolean,
  ): void {
    const arr = state.form.get('installDates') as FormArray;

    if (!state.initialized) {
      for (const d of existing) {
        const dateStr = formatUtcIsoDate(d.date);
        const isAllDay = !d.startTime && !d.endTime;

        // Split server crew allocations into foreman allocation + non-foreman crew rows.
        // The server now includes the foreman in crewAllocations; fall back to crewMemberUserIds
        // for old data that predates the allocation feature.
        const serverAllocations = (d.crewAllocations ?? []).map(a => ({ id: a.id ?? '', userId: a.userId!, userName: a.userName, allocationPercentage: a.allocationPercentage ?? null }));
        for (const a of serverAllocations) {
          if (a.userName) this.allocatedCrewNames.set(a.userId, a.userName);
        }
        const foremanAlloc = serverAllocations.find(a => a.userId === d.foremanUserId);
        const nonForemanAllocs = serverAllocations.filter(a => a.userId !== d.foremanUserId);

        const crewArray = this.fb.array(
          nonForemanAllocs.map(a => this.createCrewAllocationGroup(a.userId, a.allocationPercentage, a.id)),
        );

        const hasLaborCosts = d.hasLaborCosts === true;
        const group = this.fb.group({
          installDateId: [d.id],
          date: [dateStr, Validators.required],
          startTime: [d.startTime ?? ''],
          endTime: [d.endTime ?? ''],
          foremanUserId: [d.foremanUserId ?? '', Validators.required],
          isAllDay: [isAllDay],
          foremanAllocationPercentage: [foremanAlloc?.allocationPercentage ?? null as number | null, [Validators.min(0), Validators.max(100)]],
          hasLaborCosts: [hasLaborCosts],
          crewAllocations: crewArray,
        });
        if (this.isDateInPast(dateStr) && d.foremanUserId && d.foremanUserName) {
          this.historicalForemanNames.set(d.foremanUserId, d.foremanUserName);
        }
        this.applyRowLocks(group, dateStr, isCompleted, isLocked);
        arr.push(group);
        if (d.foremanUserId) this.ensureCrewOptionsLoaded(d.foremanUserId);
      }
      state.originalInstallDateIds = new Set(existing.map(e => e.id));
      state.originalInstallDateRows = this.snapshotRowMap(arr);
      state.initialized = true;
    }

    // Cancelled or descoped work takes no new install days. Server-existing rows above are still
    // seeded — they are worked history — but calendar picks are dropped here rather than becoming
    // rows, so a pick on locked work cannot be saved as a new future install date.
    if (isLocked) return;

    const presentKeys = new Set<string>();
    for (const c of arr.controls) {
      const date = c.get('date')?.value as string;
      const foreman = c.get('foremanUserId')?.value as string;
      presentKeys.add(`${date}|${foreman ?? ''}`);
    }

    for (const d of appended) {
      const key = `${d.date}|${d.foremanUserId}`;
      if (presentKeys.has(key)) continue;
      presentKeys.add(key);
      // No placeholder crew row — the foreman row's + adds the first one on demand.
      const crewInit = this.fb.array([] as FormGroup[]);
      const group = this.fb.group({
        installDateId: [''],
        date: [d.date, Validators.required],
        startTime: [''],
        endTime: [''],
        foremanUserId: [d.foremanUserId, Validators.required],
        isAllDay: [true],
        foremanAllocationPercentage: [null as number | null, [Validators.min(0), Validators.max(100)]],
        hasLaborCosts: [false],
        crewAllocations: crewInit,
      });
      arr.push(group);
      // Pre-go-live picks get no crew at all — skip the preferred-crew default too.
      if (this.isBeforeCrewGoLive(d.date)) {
        this.disableCrewFields(group);
        continue;
      }
      if (d.foremanUserId) {
        const rowIndex = arr.length - 1;
        const foremanId = d.foremanUserId;
        this.ensureCrewOptionsLoaded(foremanId, () => {
          const preferred = this.preferredCrew.get(foremanId) ?? [];
          if (preferred.length > 0) {
            const crewArr = arr.at(rowIndex).get('crewAllocations') as FormArray;
            crewArr.clear();
            for (const crew of preferred) {
              if (!crew.userId) continue;
              crewArr.push(this.createCrewAllocationGroup(crew.userId));
            }
          }
        });
      }
    }
  }

  /** CSV equivalent of applyInstallDates — diff-applies the CSV form-array. */
  private applyCustomerSatisfactionVisits(
    state: ProjectFormState,
    existing: CustomerSatisfactionVisitInfo[],
    appended: InstallDateFormData[],
    isLocked: boolean,
  ): void {
    const arr = state.form.get('customerSatisfactionVisits') as FormArray;

    if (!state.customerSatisfactionVisitsInitialized) {
      for (const v of existing) {
        const dateStr = formatUtcIsoDate(v.date);
        const isAllDay = !v.startTime && !v.endTime;
        const serverAllocations = v.crewAllocations ?? [];
        for (const a of serverAllocations) {
          if (a.userName) this.allocatedCrewNames.set(a.userId!, a.userName);
        }
        const foremanAlloc = serverAllocations.find(a => a.userId === v.foremanUserId);
        const nonForemanAllocations = serverAllocations.filter(a => a.userId !== v.foremanUserId);
        const hasLaborCosts = v.hasLaborCosts === true;
        const group = this.fb.group({
          installDateId: [v.id],
          date: [dateStr, Validators.required],
          startTime: [v.startTime ?? ''],
          endTime: [v.endTime ?? ''],
          foremanUserId: [v.foremanUserId ?? '', Validators.required],
          isAllDay: [isAllDay],
          foremanAllocationPercentage: [foremanAlloc?.allocationPercentage ?? null as number | null, [Validators.min(0), Validators.max(100)]],
          hasLaborCosts: [hasLaborCosts],
          crewAllocations: this.fb.array(nonForemanAllocations.map(a => this.createCrewAllocationGroup(a.userId!, a.allocationPercentage ?? null, a.id ?? ''))),
        });
        if (v.foremanUserId) {
          this.ensureCrewOptionsLoaded(v.foremanUserId);
        }
        this.applyRowLocks(group, dateStr, false, isLocked);
        arr.push(group);
      }
      state.originalCustomerSatisfactionVisitIds = new Set(existing.map(e => e.id));
      state.originalCustomerSatisfactionVisitRows = this.snapshotRowMap(arr);
      state.customerSatisfactionVisitsInitialized = true;
    }

    // See applyInstallDates: cancelled or descoped work accepts no new calendar picks.
    if (isLocked) return;

    const presentKeys = new Set<string>();
    for (const c of arr.controls) {
      const date = c.get('date')?.value as string;
      const foreman = c.get('foremanUserId')?.value as string;
      presentKeys.add(`${date}|${foreman ?? ''}`);
    }

    for (const d of appended) {
      const key = `${d.date}|${d.foremanUserId}`;
      if (presentKeys.has(key)) continue;
      presentKeys.add(key);
      // No placeholder crew row — the foreman row's + adds the first one on demand.
      const crewInit = this.fb.array([] as FormGroup[]);
      const group = this.fb.group({
        installDateId: [''],
        date: [d.date, Validators.required],
        startTime: [''],
        endTime: [''],
        foremanUserId: [d.foremanUserId, Validators.required],
        isAllDay: [true],
        foremanAllocationPercentage: [null as number | null, [Validators.min(0), Validators.max(100)]],
        hasLaborCosts: [false],
        crewAllocations: crewInit,
      });
      arr.push(group);
      // Pre-go-live picks get no crew at all — skip the preferred-crew default too.
      if (this.isBeforeCrewGoLive(d.date)) {
        this.disableCrewFields(group);
        continue;
      }
      if (d.foremanUserId) {
        const rowIndex = arr.length - 1;
        const foremanId = d.foremanUserId;
        this.ensureCrewOptionsLoaded(foremanId, () => {
          const preferred = this.preferredCrew.get(foremanId) ?? [];
          if (preferred.length > 0) {
            const crewArr = arr.at(rowIndex).get('crewAllocations') as FormArray;
            crewArr.clear();
            for (const crew of preferred) {
              if (!crew.userId) continue;
              crewArr.push(this.createCrewAllocationGroup(crew.userId));
            }
          }
        });
      }
    }
  }

  private snapshotRowMap(arr: FormArray): Map<string, InstallDateRowSnapshot> {
    return new Map(
      arr.controls.map(c => {
        const v = c.getRawValue() as InstallDateFormData;
        return [v.installDateId, snapshotInstallDateRow(v)] as const;
      }),
    );
  }

  private projectHasChanges(state: ProjectFormState, isCsvMode: boolean): boolean {
    const arrayName = isCsvMode ? 'customerSatisfactionVisits' : 'installDates';
    const originalIds = isCsvMode
      ? state.originalCustomerSatisfactionVisitIds
      : state.originalInstallDateIds;
    const arr = state.form.get(arrayName) as FormArray;
    const currentIds = new Set<string>();
    for (const c of arr.controls) {
      const id = c.get('installDateId')?.value as string;
      if (!id) return true;
      currentIds.add(id);
    }
    for (const originalId of originalIds) {
      if (!currentIds.has(originalId)) return true;
    }
    return state.form.dirty;
  }

  private todayAsDateString(): string {
    const today = new Date();
    const yyyy = today.getFullYear();
    const mm = String(today.getMonth() + 1).padStart(2, '0');
    const dd = String(today.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  }

  private isDateInPast(dateString: string): boolean {
    if (!dateString) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const date = new Date(dateString + 'T00:00:00');
    return date < today;
  }

  /**
   * Crew-lock checks, split per form array (MOD-4733). The install-dates and CSV-visits
   * loops render side by side once a project is completed, so a single index-based check
   * can't tell which array's row it's being asked about — it must be told explicitly.
   */
  isInstallDateRowCrewLocked(index: number): boolean {
    return this.isGroupCrewLocked(this.installDateArray.at(index));
  }

  isCsvRowCrewLocked(index: number): boolean {
    return this.isGroupCrewLocked(this.customerSatisfactionVisitArray.at(index));
  }

  /**
   * True when a row's crew assignment is read-only: its date is at or before the labor cut-off
   * (`hasLaborCosts`, stamped server-side from MAX(labor.ProjectCosts.Date) — a date-level
   * high-water mark, not a per-project lookup), or its date falls before the configured crew
   * go-live date. Takes the control so it can be applied to a project that isn't the one on
   * screen (row seeding runs for every project's state).
   */
  private isGroupCrewLocked(group: AbstractControl | undefined): boolean {
    if (!group) return false;
    if (group.get('hasLaborCosts')?.value === true) return true;
    return this.isBeforeCrewGoLive((group.get('date')?.value as string) ?? '');
  }

  /**
   * True when `dateString` is strictly before `ConfigService.crewGoLiveDate`. Both sides are
   * ISO `YYYY-MM-DD`, so a lexicographic compare is a date compare. No configured go-live
   * date means no cut-off.
   */
  private isBeforeCrewGoLive(dateString: string): boolean {
    const goLive = this.configService.crewGoLiveDate;
    if (!goLive || !dateString) return false;
    return dateString < goLive;
  }

  /**
   * Makes a row's crew assignment read-only while leaving its install-date fields editable.
   * Disabled controls keep their values in `getRawValue()`, so an already-saved crew list on a
   * locked row still round-trips through the save payload untouched. Empty placeholder crew
   * rows are dropped instead: a locked row offers no crew edit, so a blank picker is only noise.
   */
  private disableCrewFields(group: FormGroup): void {
    group.get('foremanAllocationPercentage')?.disable({ emitEvent: false });
    const crewArr = group.get('crewAllocations') as FormArray;
    for (let i = crewArr.length - 1; i >= 0; i--) {
      if (!crewArr.at(i).get('userId')?.value) crewArr.removeAt(i);
    }
    crewArr.disable({ emitEvent: false });
  }

  private disablePrimaryFields(group: FormGroup): void {
    for (const field of ['date', 'foremanUserId', 'isAllDay', 'startTime', 'endTime']) {
      group.get(field)?.disable({ emitEvent: false });
    }
  }

  /**
   * Seed-time row locks (MOD-4733). Past rows, rows of a completed project, and rows of
   * CANCELLED work keep their date/foreman/time read-only. Crew fields lock per
   * isGroupCrewLocked (labor cut-off / pre-go-live) — neither completion nor cancellation
   * alone locks allocations.
   *
   * `hasLaborCosts` is NOT read here: it now means only "crew is locked by the labor cut-off"
   * and reaches the crew lock through isGroupCrewLocked. It used to additionally disable the
   * whole group for a past row with imported hours, which was redundant — a past row already
   * has its date/foreman/time disabled by the branch below, and those are the only fields the
   * full disable added. Dropping it also stops the row being excluded from the save diff
   * wholesale, so a genuinely-changed sibling field can still round-trip.
   *
   * Cancellation and descoping deliberately share the past/completed branch rather than disabling
   * the whole group as it once did: a locked row's crew must stay editable so worked labour can be
   * attributed. Locking `date` is what stops a locked row being dragged forward into a new future
   * install day, which is the thing both dispositions must prevent.
   */
  private applyRowLocks(
    group: FormGroup,
    dateStr: string,
    isCompleted: boolean,
    isLocked: boolean,
  ): void {
    if (isLocked || this.isDateInPast(dateStr) || isCompleted) {
      this.disablePrimaryFields(group);
    }
    if (this.isGroupCrewLocked(group)) {
      this.disableCrewFields(group);
    }
  }

  private addOneDayToDateString(dateString: string): string {
    const date = new Date(dateString + 'T00:00:00');
    date.setDate(date.getDate() + 1);
    const yyyy = date.getFullYear();
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const dd = String(date.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  }
}
